/**
 * CommandViewLayout.tsx — W45.2 Command View (per-account)
 *
 * Three-artifact money-shot layout for Bryan's prospect demos.
 *
 * Desktop (≥1280px):  3-column side-by-side. Helix ~25% | Tower ~25% | Galaxy ~50%.
 * Tablet (768-1279px): 2-row. Top: Helix + Tower (50/50). Bottom: Galaxy full-width.
 * Mobile (<768px):     Accordion. 3 collapsible sections.
 *                      Helix expanded by default. Tower + Galaxy collapsed.
 *                      Galaxy mounts lazily (only when section is opened).
 *
 * W45.2 additions:
 *   - Enhanced header: account name + status badge + industry + M-PACT score (large) +
 *     forecast bucket badge + owner name + account picker dropdown
 *   - Cross-highlight shared state via SelectedCpContext: all three panels respond
 *     to the same selectedCp value
 *   - Route: accountId now comes from URL params (useParams) — not session state
 *
 * Data: fetches /api/command-view/:accountId for aggregated account data.
 * Forecast bucket: computed at query time from currentCp + status.
 *
 * Canon:
 *   - No exclamation points. No emojis. M-PACT hyphenated.
 *   - Tenant 1 only — account data is guarded by server requireAccountAccess.
 *   - composite_score=0 gracefully handled (gray "Not scored" state in HelixPanel).
 *   - 5-color palette: Gray #6B7280, Red #DC2626, Orange #EA580C, Yellow #EAB308, Green #16A34A.
 *   - No Amber.
 *
 * Bryan K. Short, Founder and CEO, Mi6 Group.
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import HelixPanel  from "./HelixPanel";
import TowerPanel  from "./TowerPanel";
import GalaxyPanel from "./GalaxyPanel";
import { SelectedCpContext } from "@/hooks/useSelectedCp";
import { api } from "@/lib/session";

// ─── Types ────────────────────────────────────────────────────────────────────

interface CommandViewData {
  account: {
    id: number;
    name: string;
    industry?: string;
    status?: string;
    ownerName?: string;
  };
  compositeScore: number;
  currentCp: number | null;
  answeredCps: number[];
}

interface PortfolioAccountItem {
  id: number;
  name: string;
  status: string;
  mpactScore: number;
  isTop10: boolean;
}

interface CommandViewLayoutProps {
  accountId: string;
}

// ─── Forecast bucket computation (W63 — score-driven per 2026-05-23 canon) ─────
// Canon: Commit ≥80, Upside 65-79, Best Case 50-64, Pipeline 30-49, Omit <30.
// Source: shared/schema.ts getForecastCategory + HEALTH_PALETTE.
// No weighted-percentage display. No Probable.

const FORECAST_COLOR: Record<string, string> = {
  Commit:     "#16A34A",
  Upside:     "#16A34A",  // light green maps to canon green
  "Best Case": "#EAB308",
  Pipeline:   "#EA580C",
  Omit:       "#6B7280",
  WON:        "#16A34A",
};

function computeForecastBucket(
  status: string | undefined,
  _currentCp: number | null,
  mpactScore: number
): { label: string; color: string } {
  if (status === "Won") return { label: "WON", color: "#16A34A" };
  // Score-driven per 2026-05-23 canon
  if (mpactScore >= 80) return { label: "Commit",     color: FORECAST_COLOR["Commit"] };
  if (mpactScore >= 65) return { label: "Upside",     color: FORECAST_COLOR["Upside"] };
  if (mpactScore >= 50) return { label: "Best Case",  color: FORECAST_COLOR["Best Case"] };
  if (mpactScore >= 30) return { label: "Pipeline",   color: FORECAST_COLOR["Pipeline"] };
  return { label: "Omit", color: FORECAST_COLOR["Omit"] };
}

// ─── Accordion section (mobile) ────────────────────────────────────────────────

interface AccordionSectionProps {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
  minHeight?: number;
  onOpen?: () => void;
}

function AccordionSection({
  title,
  subtitle,
  defaultOpen = false,
  children,
  minHeight = 320,
  onOpen,
}: AccordionSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  const toggle = useCallback(() => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen) onOpen?.();
  }, [open, onOpen]);

  return (
    <div
      style={{
        borderRadius: 8,
        border: "1px solid #E5E7EB",
        background: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <button
        onClick={toggle}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "inherit",
          textAlign: "left",
        }}
        aria-expanded={open}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0B0B0F", letterSpacing: "0.03em" }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {/* Chevron */}
        <svg
          width={16}
          height={16}
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.22s ease",
            flexShrink: 0,
            color: "#6B7280",
          }}
        >
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div style={{ minHeight, borderTop: "1px solid #E5E7EB" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status?: string }) {
  if (!status) return null;
  let bg = "rgba(107,114,128,0.12)";
  let color = "#6B7280";
  let border = "rgba(107,114,128,0.4)";
  if (status === "Full Opportunity") { bg = "rgba(22,163,74,0.15)"; color = "#16A34A"; border = "rgba(22,163,74,0.4)"; }
  else if (status === "Qualified Opp") { bg = "rgba(234,179,8,0.15)"; color = "#EAB308"; border = "rgba(234,179,8,0.4)"; }
  else if (status === "Prospect") { bg = "rgba(234,88,12,0.15)"; color = "#EA580C"; border = "rgba(234,88,12,0.4)"; }
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 700,
      color,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "2px 8px",
      background: bg,
      letterSpacing: "0.06em",
      textTransform: "uppercase" as const,
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    }}>
      {status}
    </span>
  );
}

// ─── Forecast bucket badge (W63 — no weighted-percentage per 2026-05-23 canon) ───────

function ForecastBadge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 800,
      color,
      border: `1px solid ${color}55`,
      borderRadius: 12,
      padding: "2px 9px",
      background: `${color}18`,
      letterSpacing: "0.08em",
      whiteSpace: "nowrap" as const,
      flexShrink: 0,
    }}>
      {label}
    </span>
  );
}

// ─── Account picker dropdown ───────────────────────────────────────────────────

interface AccountPickerProps {
  currentAccountId: string;
  accounts: PortfolioAccountItem[];
}

function AccountPicker({ currentAccountId, accounts }: AccountPickerProps) {
  const [, setLoc] = useLocation();
  const [open, setOpen] = useState(false);

  function handleSelect(id: number) {
    setOpen(false);
    setLoc(`/account/${id}/command-view`);
  }

  if (accounts.length === 0) return null;

  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          padding: "4px 10px",
          borderRadius: 14,
          background: "#FAFAF7",
          border: "1px solid #E5E7EB",
          color: "#0B0B0F",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          letterSpacing: "0.04em",
          whiteSpace: "nowrap" as const,
        }}
        title="Jump to another account"
      >
        Jump to account
        <svg width={10} height={10} viewBox="0 0 10 10" fill="none" aria-hidden="true" style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth={1.4} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 49 }}
            onClick={() => setOpen(false)}
          />
          <div style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            right: 0,
            zIndex: 50,
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 8,
            minWidth: 220,
            maxHeight: 320,
            overflowY: "auto" as const,
            boxShadow: "0 8px 32px rgba(15,23,42,0.18)",
          }}>
            {accounts.map((a) => (
              <button
                key={a.id}
                onClick={() => handleSelect(a.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  background: String(a.id) === currentAccountId ? "#EFEFEE" : "none",
                  border: "none",
                  borderBottom: "1px solid #E5E7EB",
                  cursor: "pointer",
                  textAlign: "left" as const,
                  color: String(a.id) === currentAccountId ? "#0B0B0F" : "#0B0B0F",
                }}
              >
                {a.isTop10 && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: "#EAB308", flexShrink: 0, letterSpacing: "0.06em" }}>TOP10</span>
                )}
                <span style={{ fontSize: 12, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}>{a.name}</span>
                <span style={{ fontSize: 10, color: "#6B7280", flexShrink: 0 }} title={a.mpactScore > 0 ? undefined : "Score not yet computed"}>{a.mpactScore > 0 ? a.mpactScore.toFixed(0) : "Computing"}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main layout ──────────────────────────────────────────────────────────────

export default function CommandViewLayout({ accountId }: CommandViewLayoutProps) {
  const [data, setData] = useState<CommandViewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Track whether Galaxy accordion has been opened (for lazy mount)
  const [galaxyMounted, setGalaxyMounted] = useState(false);
  // Cross-panel CP highlight (W45.2)
  const [selectedCp, setSelectedCp] = useState<number | null>(null);
  // Account picker list
  const [portfolioAccounts, setPortfolioAccounts] = useState<PortfolioAccountItem[]>([]);

  // Fetch aggregated command-view data
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api<CommandViewData>(`/api/command-view/${accountId}`)
      .then((d: CommandViewData) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Fetch portfolio account list for the picker (low priority, fire-and-forget)
  useEffect(() => {
    let cancelled = false;
    api<PortfolioAccountItem[]>("/api/command-view-portfolio")
      .then((list: PortfolioAccountItem[]) => {
        if (!cancelled) setPortfolioAccounts(list);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Stable context value
  const cpContextValue = useMemo(
    () => ({ selectedCp, setSelectedCp }),
    [selectedCp]
  );

  if (loading) {
    return (
      <div style={styles.loadingWrap}>
        <div style={styles.loadingText}>Loading Command View…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={styles.loadingWrap}>
        <div style={{ ...styles.loadingText, color: "#DC2626" }}>
          {error ? `Could not load data: ${error}` : "No data available for this account."}
        </div>
      </div>
    );
  }

  const answeredSet = new Set(data.answeredCps);
  const { account, compositeScore, currentCp } = data;
  const bucket = computeForecastBucket(account.status, currentCp, compositeScore);

  return (
    <SelectedCpContext.Provider value={cpContextValue}>
      <div style={styles.root}>
        {/* ── Enhanced account header strip ── */}
        <div style={styles.headerBar}>
          {/* Left: name + status + industry */}
          <div style={styles.headerLeft}>
            <span style={styles.headerLabel}>Command View</span>
            <span style={styles.headerSep}>·</span>
            <span style={styles.headerName}>{account.name}</span>
            <StatusBadge status={account.status} />
            {account.industry && (
              <span style={styles.headerIndustry}>{account.industry}</span>
            )}
          </div>

          {/* Center: Composite score + forecast bucket */}
          <div style={styles.headerCenter}>
            {compositeScore > 0 ? (
              <span style={styles.scoreBadge}>
                Composite{" "}
                <span style={{ fontWeight: 800, fontSize: 16, color: "#0B0B0F" }}>{Math.round(compositeScore)}</span>
                <span style={{ color: "#6B7280", fontSize: 10 }}>/100</span>
              </span>
            ) : (
              <span style={{ ...styles.scoreBadge, color: "#6B7280", borderColor: "#374151" }}>
                Not scored
              </span>
            )}
            <ForecastBadge {...bucket} />
          </div>

          {/* Right: owner name + account picker */}
          <div style={styles.headerRight}>
            {account.ownerName && (
              <span style={styles.ownerLabel}>{account.ownerName}</span>
            )}
            <AccountPicker
              currentAccountId={accountId}
              accounts={portfolioAccounts}
            />
          </div>
        </div>

        {/* ── Desktop 3-col layout ── */}
        <div className="cv-desktop-layout" style={styles.desktopGrid}>
          <div style={styles.desktopColLeft}>
            <div style={styles.panelCard}>
              <HelixPanel
                accountId={String(account.id)}
                accountName={account.name}
                industry={account.industry}
                compositeScore={compositeScore}
              />
            </div>
          </div>
          <div style={styles.desktopColCenter}>
            <div style={styles.panelCard}>
              <TowerPanel
                accountId={String(account.id)}
                accountName={account.name}
                currentCp={currentCp}
                answeredCps={answeredSet}
              />
            </div>
          </div>
          <div style={styles.desktopColRight}>
            <div style={{ ...styles.panelCard, padding: 0 }}>
              <GalaxyPanel
                accountId={String(account.id)}
                accountName={account.name}
                suppressMount={false}
              />
            </div>
          </div>
        </div>

        {/* ── Tablet 2-row layout ── */}
        <div className="cv-tablet-layout" style={styles.tabletGrid}>
          <div style={styles.tabletTopRow}>
            <div style={{ ...styles.panelCard, flex: 1 }}>
              <HelixPanel
                accountId={String(account.id)}
                accountName={account.name}
                industry={account.industry}
                compositeScore={compositeScore}
              />
            </div>
            <div style={{ ...styles.panelCard, flex: 1 }}>
              <TowerPanel
                accountId={String(account.id)}
                accountName={account.name}
                currentCp={currentCp}
                answeredCps={answeredSet}
              />
            </div>
          </div>
          <div style={styles.tabletBottomRow}>
            <div style={{ ...styles.panelCard, padding: 0, width: "100%", minHeight: 480 }}>
              <GalaxyPanel
                accountId={String(account.id)}
                accountName={account.name}
                suppressMount={false}
              />
            </div>
          </div>
        </div>

        {/* ── Mobile accordion layout ── */}
        <div className="cv-mobile-layout" style={styles.mobileStack}>
          <AccordionSection
            title="Opportunity Health"
            subtitle="DNA — Composite score by checkpoint"
            defaultOpen={true}
            minHeight={360}
          >
            <div style={{ height: 360 }}>
              <HelixPanel
                accountId={String(account.id)}
                accountName={account.name}
                industry={account.industry}
                compositeScore={compositeScore}
              />
            </div>
          </AccordionSection>

          <AccordionSection
            title="Process Elevation"
            subtitle="Detroit tower — current checkpoint floor"
            defaultOpen={false}
            minHeight={380}
          >
            <div style={{ height: 380 }}>
              <TowerPanel
                accountId={String(account.id)}
                accountName={account.name}
                currentCp={currentCp}
                answeredCps={answeredSet}
              />
            </div>
          </AccordionSection>

          <AccordionSection
            title="Flowchart — Account Path"
            subtitle="Galaxy — questions remaining and routing"
            defaultOpen={false}
            minHeight={420}
            onOpen={() => setGalaxyMounted(true)}
          >
            <div style={{ height: 420 }}>
              <GalaxyPanel
                accountId={String(account.id)}
                accountName={account.name}
                suppressMount={!galaxyMounted}
              />
            </div>
          </AccordionSection>
        </div>

        {/* Responsive display rules injected inline */}
        <style>{`
          .cv-desktop-layout { display: flex !important; }
          .cv-tablet-layout  { display: none  !important; }
          .cv-mobile-layout  { display: none  !important; }

          @media (max-width: 1279px) {
            .cv-desktop-layout { display: none  !important; }
            .cv-tablet-layout  { display: flex  !important; }
          }
          @media (max-width: 767px) {
            .cv-desktop-layout { display: none  !important; }
            .cv-tablet-layout  { display: none  !important; }
            .cv-mobile-layout  { display: flex  !important; }
          }
        `}</style>
      </div>
    </SelectedCpContext.Provider>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    background: "#FFFFFF",
    color: "#0B0B0F",
    fontFamily: "Inter, system-ui, sans-serif",
    overflow: "hidden",
    minHeight: 0,
  },
  loadingWrap: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#FFFFFF",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  loadingText: {
    fontSize: 13,
    color: "#8E8E94",
    letterSpacing: "0.04em",
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 16px",
    borderBottom: "1px solid #E5E7EB",
    background: "#FAFAF7",
    flexShrink: 0,
    gap: 10,
    flexWrap: "nowrap" as const,
    minHeight: 48,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    overflow: "hidden",
    flex: "1 1 auto",
    minWidth: 0,
  },
  headerCenter: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  headerLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase" as const,
    color: "#6B7280",
    whiteSpace: "nowrap" as const,
    flexShrink: 0,
  },
  headerSep: {
    fontSize: 12,
    color: "#D1D5DB",
    flexShrink: 0,
  },
  headerName: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0B0B0F",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    flexShrink: 1,
    minWidth: 0,
  },
  headerIndustry: {
    fontSize: 11,
    color: "#6B7280",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
    flexShrink: 2,
  },
  scoreBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: "#0B0B0F",
    border: "1px solid #E5E7EB",
    borderRadius: 14,
    padding: "3px 10px",
    background: "#FAFAF7",
    whiteSpace: "nowrap" as const,
    letterSpacing: "0.04em",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  },
  ownerLabel: {
    fontSize: 11,
    color: "#6B7280",
    whiteSpace: "nowrap" as const,
  },
  // ── Desktop ──
  desktopGrid: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: 0,
    overflow: "hidden",
  },
  desktopColLeft: {
    width: "25%",
    minWidth: 220,
    borderRight: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  desktopColCenter: {
    width: "25%",
    minWidth: 280, // W66.X6: tower image requires at least 280px to render clearly
    borderRight: "1px solid #E5E7EB",
    overflow: "hidden",
  },
  desktopColRight: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
  },
  panelCard: {
    width: "100%",
    height: "100%",
    background: "#FFFFFF",
    overflow: "hidden",
  },
  // ── Tablet ──
  tabletGrid: {
    flex: 1,
    minHeight: 0,
    flexDirection: "column" as const,
    overflow: "auto",
    gap: 0,
  },
  tabletTopRow: {
    display: "flex",
    height: 420,
    flexShrink: 0,
    borderBottom: "1px solid #E5E7EB",
    overflow: "hidden",
    gap: 0,
  },
  tabletBottomRow: {
    flex: 1,
    minHeight: 480,
    overflow: "hidden",
    display: "flex",
  },
  // ── Mobile ──
  mobileStack: {
    flex: 1,
    flexDirection: "column" as const,
    padding: 12,
    gap: 8,
    overflowY: "auto" as const,
    minHeight: 0,
  },
} as const;
